import { useState, useCallback } from 'react';
import { Button, Space, Tooltip, Divider, Modal, InputNumber, message } from 'antd';
import {
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignMiddleOutlined,
  VerticalAlignBottomOutlined,
  ColumnWidthOutlined,
  GroupOutlined,
  UngroupOutlined,
  UpOutlined,
  DownOutlined,
  BorderBottomOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons';
import * as THREE from 'three';
import useSceneStore from '../../stores/sceneStore';
import useSelectionStore from '../../stores/selectionStore';
import useLayerStore from '../../stores/layerStore';
import { modelRefsRegistry } from '../../stores/modelRefsRegistry';
import {
  alignItems,
  distributeItems,
  matrixOffsets,
  ALIGN_LABEL,
  DISTRIBUTE_LABEL,
  type AlignType,
  type DistributeType,
  type LayoutItem,
} from '../../utils/layoutUtil';

// 左侧树"工具"面板：对齐(9向)/分布(3向)/阵列/排序/分组/解组
export default function LayoutToolbar() {
  const { models, updateModel, addModel, moveModel } = useSceneStore();
  const { selectedIds, deselectAll, select } = useSelectionStore();
  const { addLayer, layers, selectedLayerId, removeLayer } = useLayerStore();
  const [arrayOpen, setArrayOpen] = useState(false);
  const [arrayCols, setArrayCols] = useState(2);
  const [arrayRows, setArrayRows] = useState(2);
  const [arrayGapX, setArrayGapX] = useState(2);
  const [arrayGapZ, setArrayGapZ] = useState(2);

  const multi = selectedIds.length >= 2;
  const many = selectedIds.length >= 3;
  const single = selectedIds.length === 1;

  // 获取选中模型的包围盒（优先真实 mesh，缺省单点）
  const getLayoutItems = useCallback((): LayoutItem[] => {
    return selectedIds
      .map((id) => models.find((m) => m.id === id))
      .filter((m): m is NonNullable<typeof m> => !!m)
      .map((m) => {
        const handle = modelRefsRegistry.get(m.id);
        let box: LayoutItem['box'];
        if (handle?.group) {
          const b = new THREE.Box3().setFromObject(handle.group);
          if (!b.isEmpty()) {
            box = { min: b.min, max: b.max };
          }
        }
        return { id: m.id, position: [...m.position], box };
      });
  }, [selectedIds, models]);

  const handleAlign = useCallback((type: AlignType) => {
    const items = getLayoutItems();
    if (items.length < 2) return;
    const result = alignItems(items, type);
    result.forEach((pos, id) => updateModel(id, { position: pos }));
    message.success(ALIGN_LABEL[type]);
  }, [getLayoutItems, updateModel]);

  const handleDistribute = useCallback((type: DistributeType) => {
    const items = getLayoutItems();
    if (items.length < 3) return;
    const result = distributeItems(items, type);
    result.forEach((pos, id) => updateModel(id, { position: pos }));
    message.success(DISTRIBUTE_LABEL[type]);
  }, [getLayoutItems, updateModel]);

  const handleArray = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const src = models.find((m) => m.id === selectedIds[0]);
    if (!src) return;
    const item: LayoutItem = { id: src.id, position: [...src.position] };
    const offsets = matrixOffsets(item, { cols: arrayCols, rows: arrayRows, gapX: arrayGapX, gapZ: arrayGapZ });
    offsets.forEach((position) => {
      addModel({
        name: src.name,
        url: src.url,
        format: src.format,
        presetId: src.presetId,
        position,
        rotation: [...src.rotation],
        scale: [...src.scale],
        visible: src.visible,
        locked: src.locked,
        materials: src.materials,
      } as never);
    });
    setArrayOpen(false);
    message.success(`已生成 ${offsets.length} 个阵列副本`);
  }, [selectedIds, models, arrayCols, arrayRows, arrayGapX, arrayGapZ, addModel]);

  const handleGroup = useCallback(() => {
    if (selectedIds.length < 2) return;
    addLayer({
      name: `分组 ${selectedIds.length} 个对象`,
      type: 'group',
      visible: true,
      locked: false,
      modelIds: [...selectedIds],
    });
    deselectAll();
    message.success('已创建分组（可在图层面板查看/锁定）');
  }, [selectedIds, addLayer, deselectAll]);

  const handleUngroup = useCallback(() => {
    const target = layers.find((l) => l.id === selectedLayerId);
    if (!target || target.type !== 'group') {
      message.warning('请先在「图层」面板选中要解散的分组');
      return;
    }
    removeLayer(selectedLayerId as string);
    message.success('已解散分组');
  }, [layers, selectedLayerId, removeLayer]);

  const handleSort = useCallback((dir: 'up' | 'down' | 'top' | 'bottom') => {
    if (!single) return;
    moveModel(selectedIds[0], dir);
  }, [single, selectedIds, moveModel]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* 对齐（9 向） */}
      <div style={{ fontSize: 12, color: '#888' }}>对齐（至少选 2 个模型）</div>
      <Space wrap size={4}>
        <Tooltip title="X 左对齐">
          <Button size="small" icon={<AlignLeftOutlined />} disabled={!multi} onClick={() => handleAlign('x-left')} />
        </Tooltip>
        <Tooltip title="X 水平居中">
          <Button size="small" icon={<AlignCenterOutlined />} disabled={!multi} onClick={() => handleAlign('x-center')} />
        </Tooltip>
        <Tooltip title="X 右对齐">
          <Button size="small" icon={<AlignRightOutlined />} disabled={!multi} onClick={() => handleAlign('x-right')} />
        </Tooltip>
        <Tooltip title="Z 前对齐">
          <Button size="small" disabled={!multi} onClick={() => handleAlign('z-front')}>Z前</Button>
        </Tooltip>
        <Tooltip title="Z 纵深居中">
          <Button size="small" disabled={!multi} onClick={() => handleAlign('z-center')}>Z中</Button>
        </Tooltip>
        <Tooltip title="Z 后对齐">
          <Button size="small" disabled={!multi} onClick={() => handleAlign('z-back')}>Z后</Button>
        </Tooltip>
        <Tooltip title="Y 底部贴地对齐">
          <Button size="small" icon={<VerticalAlignBottomOutlined />} disabled={!multi} onClick={() => handleAlign('y-bottom')} />
        </Tooltip>
        <Tooltip title="Y 垂直居中">
          <Button size="small" icon={<VerticalAlignMiddleOutlined />} disabled={!multi} onClick={() => handleAlign('y-center')} />
        </Tooltip>
        <Tooltip title="Y 顶部对齐">
          <Button size="small" icon={<VerticalAlignTopOutlined />} disabled={!multi} onClick={() => handleAlign('y-top')} />
        </Tooltip>
      </Space>

      <Divider style={{ margin: '4px 0', borderColor: '#30475e' }} />

      {/* 分布（3 向） */}
      <div style={{ fontSize: 12, color: '#888' }}>分布（至少选 3 个模型）</div>
      <Space size={4}>
        <Tooltip title="X 水平均分">
          <Button size="small" icon={<ColumnWidthOutlined />} disabled={!many} onClick={() => handleDistribute('x-even')} />
        </Tooltip>
        <Tooltip title="Z 纵深均分">
          <Button size="small" disabled={!many} onClick={() => handleDistribute('z-even')}>Z均分</Button>
        </Tooltip>
        <Tooltip title="Y 垂直均分">
          <Button size="small" disabled={!many} onClick={() => handleDistribute('y-even')}>Y均分</Button>
        </Tooltip>
      </Space>

      <Divider style={{ margin: '4px 0', borderColor: '#30475e' }} />

      {/* 阵列 / 分组 / 排序 */}
      <Space wrap size={4}>
        <Tooltip title="矩阵阵列（选中 1 个模型生成机柜组）">
          <Button size="small" icon={<AppstoreAddOutlined />} disabled={!single} onClick={() => setArrayOpen(true)} />
        </Tooltip>
        <Tooltip title="打组（生成分组，可在图层面板锁定）">
          <Button size="small" icon={<GroupOutlined />} disabled={!multi} onClick={handleGroup} />
        </Tooltip>
        <Tooltip title="解组（先在图层面板选中分组）">
          <Button size="small" icon={<UngroupOutlined />} onClick={handleUngroup} />
        </Tooltip>
        <Divider orientation="vertical" style={{ borderColor: '#30475e' }} />
        <Tooltip title="上移一层">
          <Button size="small" icon={<UpOutlined />} disabled={!single} onClick={() => handleSort('up')} />
        </Tooltip>
        <Tooltip title="下移一层">
          <Button size="small" icon={<DownOutlined />} disabled={!single} onClick={() => handleSort('down')} />
        </Tooltip>
        <Tooltip title="置顶">
          <Button size="small" icon={<VerticalAlignTopOutlined />} disabled={!single} onClick={() => handleSort('top')} />
        </Tooltip>
        <Tooltip title="置底">
          <Button size="small" icon={<BorderBottomOutlined />} disabled={!single} onClick={() => handleSort('bottom')} />
        </Tooltip>
      </Space>

      <Modal
        title="矩阵阵列"
        open={arrayOpen}
        onOk={handleArray}
        onCancel={() => setArrayOpen(false)}
        okText="生成"
        cancelText="取消"
        width={360}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Space>
            <span style={{ color: '#aaa' }}>列数</span>
            <InputNumber size="small" min={1} max={20} value={arrayCols} onChange={(v) => setArrayCols(v ?? 1)} />
            <span style={{ color: '#aaa' }}>行数</span>
            <InputNumber size="small" min={1} max={20} value={arrayRows} onChange={(v) => setArrayRows(v ?? 1)} />
          </Space>
          <Space>
            <span style={{ color: '#aaa' }}>X 间距</span>
            <InputNumber size="small" min={0} max={100} value={arrayGapX} onChange={(v) => setArrayGapX(v ?? 0)} />
            <span style={{ color: '#aaa' }}>Z 间距</span>
            <InputNumber size="small" min={0} max={100} value={arrayGapZ} onChange={(v) => setArrayGapZ(v ?? 0)} />
          </Space>
          <div style={{ color: '#888', fontSize: 11 }}>将以选中模型为源，生成 {arrayCols * arrayRows - 1} 个副本（不含源）</div>
        </Space>
      </Modal>
    </div>
  );
}